import { type ReactElement } from 'react'
import { LOCALES, LOCALE_PREFIX } from './index'
import { ROUTES, type StaticPageKey, type PageKey, localizedPath } from './routes'
import { RouteMeta } from './LocaleContext'

export type GeneratedRoute = { path: string; element: ReactElement }

/** Wrappe un élément de page dans le RouteMeta de son pageKey. */
function wrap(pageKey: PageKey, element: ReactElement): ReactElement {
  return <RouteMeta pageKey={pageKey}>{element}</RouteMeta>
}

/**
 * Construit les routes statiques pour les 3 langues.
 * `pages` mappe chaque pageKey statique à son composant React.
 */
export function buildStaticRoutes(
  pages: Record<StaticPageKey, ReactElement>,
): GeneratedRoute[] {
  const routes: GeneratedRoute[] = []
  for (const locale of LOCALES) {
    for (const key of Object.keys(pages) as StaticPageKey[]) {
      routes.push({
        path: localizedPath(key, locale),
        element: wrap(key, pages[key]),
      })
    }
  }
  return routes
}

/**
 * Construit les routes dynamiques `<segment>/:slug` pour les 3 langues.
 * `segmentKey` est la clé registre statique du segment parent ('blog', 'sectors').
 * `pageKey` est la clé dynamique de la page de détail ('blog.article', 'sectors.detail').
 */
export function buildDynamicRoutes(
  segmentKey: StaticPageKey,
  pageKey: PageKey,
  element: ReactElement,
): GeneratedRoute[] {
  const routes: GeneratedRoute[] = []
  for (const locale of LOCALES) {
    const prefix = LOCALE_PREFIX[locale]
    const seg = ROUTES[segmentKey][locale]
    routes.push({
      path: `${prefix}/${seg}/:slug`,
      element: wrap(pageKey, element),
    })
  }
  return routes
}
